import {
  Constructor,
  type ILabel,
  type IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
} from '@ardoq/yfiles';
import { type GraphNode, LabelExportSupport } from '@ardoq/graph';
import {
  type LabelStyleProperties,
  createSingleLabelExporter,
} from '@ardoq/export';
import {
  measureNodeLabel,
  MODERNIZED_BLOCK_DIAGRAM_LINE_HEIGHT as LINE_HEIGHT,
} from '@ardoq/graph-labeling';
import { CanvasLabelVisual } from '../../labels/CanvasLabelVisual';
import {
  MODERNIZED_BLOCK_DIAGRAM_NODE_WIDTH as NODE_WIDTH,
  MODERNIZED_BLOCK_DIAGRAM_NODE_HEIGHT as NODE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_PADDING as PADDING,
  MODERNIZED_BLOCK_DIAGRAM_LEAF_NODE_SHAPE_HEIGHT as SHAPE_HEIGHT,
  MODERNIZED_BLOCK_DIAGRAM_LABEL_MAX_LINES,
} from 'yfilesExtensions/styles/modernized/consts';
import { MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_MAX_WIDTH } from 'yfilesExtensions/styles/consts';
import { wrapString } from 'tabview/blocks/misc/string';
import { createLabelStyle, getOpacity, Preset } from '../../shared/styleUtils';
import { getLeafNodeLayout, getNodeFromLabel } from '../../shared/layoutUtils';

class LeafNodeLabelStyle extends LabelStyleBase {
  static Instance = new LeafNodeLabelStyle();
  /**
   * the preferred label size to report to the graph layout.
   *
   * because this label style uses an interior layout parameter, and because the layout has considerNodeLabels=false, the preferred size does not need to be the accurate size of the label.
   *
   * rather than calculate the size of the text, we just return the full node width and height.
   *
   * this is preferable to returning zero size, because that would cause the label to be omitted by the canvas renderer when its origin point is offscreen. we tell the graph that this label takes up the full size of the node, and so we ensure the visual will always be painted when part of the node is in view.
   */
  private static PreferredSize = new Size(NODE_WIDTH, NODE_HEIGHT);

  public static getStyle(
    _context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties | null {
    const { text } = label;
    const node = getNodeFromLabel(label);
    if (!node) {
      return null;
    }

    const { isTransparentized } = node.tag as GraphNode;

    const wrappedLabel = wrapString(
      text,
      MODERNIZED_BLOCK_DIAGRAM_EDGE_LABEL_MAX_WIDTH,
      measureNodeLabel,
      MODERNIZED_BLOCK_DIAGRAM_LABEL_MAX_LINES
    );

    return createLabelStyle(
      null,
      { kind: 'lines', lines: wrappedLabel },
      { preset: Preset.Modern, isTransparentized }
    );
  }

  public static getLayout(
    context: IRenderContext,
    label: ILabel,
    forExport = false
  ): Rect {
    const wrappedLabel = label.tag as string[] | null;
    if (!wrappedLabel) {
      return Rect.EMPTY;
    }

    const node = getNodeFromLabel(label);
    if (!node) {
      return Rect.EMPTY;
    }

    const { x: nodeX, y: nodeY, width: nodeWidth } = node.layout;
    const { id } = node.tag as GraphNode;

    const leafNodeLayout = getLeafNodeLayout(context);
    if (!leafNodeLayout) {
      return Rect.EMPTY;
    }

    const layout = leafNodeLayout.get(id);
    if (!layout) {
      return Rect.EMPTY;
    }

    const { shapeY } = layout;
    const mainLabelHeight = wrappedLabel.length * LINE_HEIGHT;

    // Adjust y position for export to account for Lucid's default padding
    const yAdjustment = forExport ? -5 : 0;

    return new Rect(
      nodeX + PADDING,
      nodeY + SHAPE_HEIGHT + shapeY + PADDING + yAdjustment,
      nodeWidth - 2 * PADDING,
      mainLabelHeight
    );
  }

  private constructor() {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): any {
    if (type === LabelExportSupport) {
      const provider: LabelExportSupport = {
        toJSON: createSingleLabelExporter(
          (context: IRenderContext, label: ILabel) =>
            LeafNodeLabelStyle.getLayout(context, label, true),
          LeafNodeLabelStyle.getStyle
        ),
      };
      return provider;
    }
    return super.lookup(label, type);
  }

  protected override createVisual(context: IRenderContext, label: ILabel) {
    const wrappedLabel = label.tag as string[] | null;
    if (!wrappedLabel) {
      return null;
    }

    const bounds = LeafNodeLabelStyle.getLayout(context, label, false);
    if (bounds.isEmpty) {
      return null;
    }

    const node = getNodeFromLabel(label);
    if (!node) {
      return null;
    }

    const { isTransparentized } = node.tag as GraphNode;
    return new CanvasLabelVisual(
      bounds,
      null,
      wrappedLabel,
      getOpacity(isTransparentized)
    );
  }

  protected override getPreferredSize(_: ILabel): Size {
    return LeafNodeLabelStyle.PreferredSize;
  }
}

export { LeafNodeLabelStyle };
